/**
 * ET4: ritar etikettsidorna på canvas i exakta mm och gör en PDF av dem (labelPdf.ts). Används av
 * apparna, där utskriften av webbvyn inte går att lita på. Korten ritas med samma mått, typsnitt och
 * färger som LabelCard/EquipLabelCard i utskriftsläge (CSS-px, 96 per tum), så PDF:en ser ut som arket.
 */
import QRCode from 'qrcode';
import { labelPricing, type PriceField } from './labelLayout';
import { buildImagePdf, type PdfImagePage, type PdfPageLayout } from './labelPdf';

/** Det som behövs av en etikett för att rita den (en delmängd av LabelData). */
export interface DrawableLabel {
  id: string;
  species: string;
  cut: string;
  markName: string;
  styckatDate: string;
  weightKg: string;
  kgPrice?: string;
  totalPrice?: string;
  notes: string;
  qrValue: string;
  customImageUrl?: string;
  imgX?: number;
  imgY?: number;
  imgScale?: number;
}

export interface DrawOptions {
  template: 'vilt' | 'equip';
  weightByHand: boolean;
  equipEmojis?: Record<string, string>;
}

const LBL = {
  bg: '#ffffff', ink: '#1d261d', sub: '#3f473c', muted: '#6b7166',
  accent: '#a4560a', qrDark: '#1a2a1a', equipBlue: '#0a6fb0', teal: '#16a594',
};
const FONT = "'Hanken Grotesk', system-ui, -apple-system, sans-serif";
const MM_TO_PX = 96 / 25.4;
/** Upplösning för sidbilderna. */
const DPI = 300;
const Q_LOGO_SRC = '/qvenari-logo.png';

type Ctx = CanvasRenderingContext2D;

function font(ctx: Ctx, weight: number, size: number, italic = false, family = FONT) {
  ctx.font = `${italic ? 'italic ' : ''}${weight} ${size}px ${family}`;
}

/** Text med extra teckenavstånd (letter-spacing i px) – canvas har det inte överallt. */
function spacedWidth(ctx: Ctx, text: string, spacing: number): number {
  return spacing ? [...text].reduce((w, ch) => w + ctx.measureText(ch).width + spacing, 0) : ctx.measureText(text).width;
}
function fillSpaced(ctx: Ctx, text: string, x: number, y: number, spacing: number) {
  if (!spacing) { ctx.fillText(text, x, y); return; }
  for (const ch of text) { ctx.fillText(ch, x, y); x += ctx.measureText(ch).width + spacing; }
}

/** Kortar texten med … så att den ryms (text-overflow: ellipsis). */
function fit(ctx: Ctx, text: string, maxW: number, spacing = 0): string {
  if (spacedWidth(ctx, text, spacing) <= maxW) return text;
  let t = text;
  while (t.length > 0 && spacedWidth(ctx, `${t}…`, spacing) > maxW) t = t.slice(0, -1);
  return t ? `${t.trimEnd()}…` : '';
}

/** Skriver en rad i en radruta (top, lineH) – vertikalt centrerad som i CSS. */
function line(ctx: Ctx, text: string, x: number, top: number, lineH: number, maxW: number, spacing = 0) {
  ctx.textBaseline = 'middle';
  fillSpaced(ctx, fit(ctx, text, maxW, spacing), x, top + lineH / 2, spacing);
}

const images = new Map<string, Promise<HTMLImageElement | null>>();
function loadImage(src: string): Promise<HTMLImageElement | null> {
  let p = images.get(src);
  if (!p) {
    p = new Promise(resolve => {
      const img = new Image();
      // Bilder från andra adresser får inte göra canvasen oläsbar (toBlob)
      if (!src.startsWith('data:') && !src.startsWith('/')) img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = src;
    });
    images.set(src, p);
  }
  return p;
}

/** QR-koden med Q-loggan i mitten, som QRWithLogo (marginal 1 modul, felkorrigering H). */
function drawQr(ctx: Ctx, value: string, x: number, y: number, size: number, logo: HTMLImageElement | null) {
  const qr = QRCode.create(value || 'https://qvenari.com', { errorCorrectionLevel: 'H' });
  const n = qr.modules.size;
  const cell = size / (n + 2);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(x, y, size, size);
  ctx.fillStyle = LBL.qrDark;
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      // Lite överlapp så att inga hårstrån syns mellan modulerna
      if (qr.modules.get(r, c)) ctx.fillRect(x + (c + 1) * cell, y + (r + 1) * cell, cell + 0.05, cell + 0.05);
    }
  }
  const cx = x + size / 2, cy = y + size / 2, rad = size * 0.15;
  ctx.beginPath();
  ctx.arc(cx, cy, rad, 0, Math.PI * 2);
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  if (logo) {
    const h = rad * 1.3;
    const w = h * (logo.naturalWidth / (logo.naturalHeight || 1));
    ctx.drawImage(logo, cx - w / 2, cy - h / 2, w, h);
  }
}

/** Egen bild på etiketten: centrerad på (imgX %, imgY %), bredd 32 % × skala, 65 % opacitet. */
function drawCustomImage(ctx: Ctx, l: DrawableLabel, w: number, h: number, img: HTMLImageElement | null) {
  if (!img) return;
  const iw = w * (32 * (l.imgScale ?? 1)) / 100;
  const ih = iw * (img.naturalHeight / (img.naturalWidth || 1));
  ctx.save();
  ctx.globalAlpha = 0.65;
  ctx.drawImage(img, w * (l.imgX ?? 82) / 100 - iw / 2, h * (l.imgY ?? 32) / 100 - ih / 2, iw, ih);
  ctx.restore();
}

function roundRect(ctx: Ctx, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Viltetikett (LabelCard, printMode). w, h i CSS-px. */
function drawVilt(ctx: Ctx, l: DrawableLabel, w: number, h: number, weightByHand: boolean, logo: HTMLImageElement | null) {
  const padX = 9, padY = 7, gap = 5, qr = 62;
  const innerW = w - 2 * padX;
  const leftW = innerW - qr - 8;
  let y = padY;

  font(ctx, 800, 14);
  ctx.fillStyle = LBL.accent;
  line(ctx, (l.species || '—').toUpperCase(), padX, y, 14 * 1.15, leftW, 14 * 0.08);
  y += 14 * 1.15 + 1;
  font(ctx, 800, 24);
  ctx.fillStyle = LBL.ink;
  line(ctx, l.cut || '—', padX, y, 24 * 1.1, leftW);
  y += 24 * 1.1;
  if (l.markName) {
    y += 2;
    font(ctx, 400, 13);
    ctx.fillStyle = LBL.sub;
    line(ctx, l.markName, padX, y, 13 * 1.3, leftW);
    y += 13 * 1.3;
  }
  // Styckat + datum (eller en linje att skriva på), på samma baslinje
  y += 3;
  const rowH = 14 * 1.3;
  const base = y + rowH * 0.75;
  ctx.textBaseline = 'alphabetic';
  font(ctx, 700, 11);
  ctx.fillStyle = LBL.muted;
  fillSpaced(ctx, 'STYCKAT', padX, base, 11 * 0.07);
  const capsW = spacedWidth(ctx, 'STYCKAT', 11 * 0.07);
  if (l.styckatDate) {
    font(ctx, 600, 14);
    ctx.fillStyle = LBL.ink;
    ctx.fillText(l.styckatDate, padX + capsW + 6, base);
  } else {
    ctx.fillStyle = LBL.ink;
    ctx.fillRect(padX + capsW + 6, base, 90, 1.5);
  }
  y += rowH;

  drawQr(ctx, l.qrValue, w - padX - qr, padY, qr, logo);

  // Kilopris, vikt och pris (ET5)
  const notesH = l.notes ? 12 * 1.3 : 0;
  const boxTop = Math.max(y, padY + qr) + gap;
  const boxBottom = h - padY - (notesH ? notesH + gap : 0);
  const boxH = Math.max(44, boxBottom - boxTop);
  const pricing = labelPricing(l, weightByHand);
  const fields = ([
    ['kgPrice', 'KILOPRIS', pricing.kgPrice, 'kr/kg', 1],
    ['weight', 'VIKT', pricing.weight, 'kg', 1.4],
    ['price', 'PRIS', pricing.price, 'kr', 1.1],
  ] as Array<[string, string, PriceField, string, number]>).filter(([, , f]) => f.mode !== 'hidden');
  const grow = fields.reduce((s, f) => s + f[4], 0);
  const free = innerW - (fields.length - 1) * gap;
  let bx = padX;
  for (const [key, caption, f, unit, g] of fields) {
    const bw = free * g / grow;
    const lw = key === 'weight' ? 2 : 1.5;
    ctx.strokeStyle = LBL.ink;
    ctx.lineWidth = lw;
    roundRect(ctx, bx + lw / 2, boxTop + lw / 2, bw - lw, boxH - lw, 4);
    ctx.stroke();
    font(ctx, 700, 11);
    ctx.fillStyle = LBL.muted;
    line(ctx, caption, bx + lw + 6, boxTop + lw + 2, 11 * 1.2, bw - 2 * lw - 12, 11 * 0.07);
    const vs = key === 'weight' ? 22 : 18, us = key === 'weight' ? 14 : 11;
    const bottom = boxTop + boxH - lw - 3;
    ctx.textBaseline = 'alphabetic';
    font(ctx, 700, us);
    ctx.fillStyle = LBL.ink;
    const uw = ctx.measureText(unit).width;
    ctx.fillText(unit, bx + bw - lw - 6 - uw, bottom - us * 0.12);
    if (f.text) {
      font(ctx, 800, vs);
      ctx.fillStyle = LBL.ink;
      ctx.fillText(fit(ctx, f.text, bw - 2 * lw - 12 - uw - 3), bx + lw + 6, bottom - vs * 0.2);
    }
    bx += bw + gap;
  }

  if (l.notes) {
    font(ctx, 400, 12, true);
    ctx.fillStyle = LBL.muted;
    line(ctx, l.notes, padX, h - padY - notesH, notesH, innerW);
  }
}

/** Utrustningsetikett (EquipLabelCard, printMode). */
function drawEquip(ctx: Ctx, l: DrawableLabel, w: number, h: number, emojis: Record<string, string>, logo: HTMLImageElement | null) {
  const grad = ctx.createLinearGradient(0, 0, w, 0);
  grad.addColorStop(0, LBL.accent);
  grad.addColorStop(1, LBL.teal);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, 3);

  const padX = 8, padTop = 8, padBottom = 5, qr = 66;
  const leftW = w - 2 * padX - qr - 8;
  let y = padTop;
  font(ctx, 800, 26);
  ctx.fillStyle = LBL.equipBlue;
  line(ctx, `${emojis[l.species] || '⚙️'} ${(l.species || '—').toUpperCase()}`, padX, y, 26 * 1.25, leftW, 26 * 0.04);
  y += 26 * 1.25 + 3;
  font(ctx, 600, 20);
  ctx.fillStyle = LBL.ink;
  line(ctx, l.cut || '—', padX, y, 20 * 1.25, leftW);
  y += 20 * 1.25 + 2;
  font(ctx, 400, 20);
  ctx.fillStyle = LBL.sub;
  line(ctx, l.markName || 'Ägare okänd', padX, y, 20 * 1.25, leftW);
  y += 20 * 1.25;
  drawQr(ctx, l.qrValue || 'https://qvenari.se', w - padX - qr, padTop, qr, logo);
  const topH = Math.max(y, padTop + qr);

  // justify-content: space-between – ID-raden i mitten, anteckningen längst ned
  const idH = 18 * 1.25 + 4;
  const notesH = l.notes ? 18 * 1.25 + 2 : 0;
  const idTop = notesH ? topH + (h - padBottom - topH - idH - notesH) / 2 : h - padBottom - idH;
  ctx.textBaseline = 'alphabetic';
  font(ctx, 400, 16);
  ctx.fillStyle = LBL.muted;
  fillSpaced(ctx, 'ID', padX, idTop + 4 + 18 * 0.95, 16 * 0.06);
  const idW = spacedWidth(ctx, 'ID', 16 * 0.06);
  font(ctx, 400, 18, false, 'monospace');
  ctx.fillStyle = LBL.sub;
  ctx.fillText(l.id.slice(0, 8).toUpperCase(), padX + idW + 8, idTop + 4 + 18 * 0.95);
  if (l.notes) {
    font(ctx, 400, 18, true);
    ctx.fillStyle = LBL.muted;
    line(ctx, l.notes, padX, h - padBottom - notesH + 2, 18 * 1.25, w - 2 * padX);
  }
}

async function ensureFonts() {
  const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
  if (!fonts) return;
  await Promise.all([400, 600, 700, 800].map(wt => fonts.load(`${wt} 20px 'Hanken Grotesk'`).catch(() => [])));
}

function canvasJpeg(canvas: HTMLCanvasElement): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(b => {
      if (!b) { reject(new Error('Sidan kunde inte ritas')); return; }
      b.arrayBuffer().then(buf => resolve(new Uint8Array(buf)), reject);
    }, 'image/jpeg', 0.92);
  });
}

/** Ritar sidorna (mått i mm) och ger PDF-filen. */
export async function renderLabelPdf(pages: PdfPageLayout<DrawableLabel>[], opts: DrawOptions): Promise<Uint8Array> {
  await ensureFonts();
  const logo = await loadImage(Q_LOGO_SRC);
  const pxPerMm = DPI / 25.4;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Utskriften kunde inte ritas (canvas saknas)');
  const out: PdfImagePage[] = [];
  for (const page of pages) {
    canvas.width = Math.round(page.wMm * pxPerMm);
    canvas.height = Math.round(page.hMm * pxPerMm);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    for (const cell of page.cells) {
      const l = cell.item;
      const w = cell.w * MM_TO_PX, h = cell.h * MM_TO_PX;
      const img = l.customImageUrl ? await loadImage(l.customImageUrl) : null;
      ctx.save();
      ctx.translate(cell.x * pxPerMm, cell.y * pxPerMm);
      ctx.scale(pxPerMm / MM_TO_PX, pxPerMm / MM_TO_PX);
      ctx.beginPath();
      ctx.rect(0, 0, w, h);
      ctx.clip();
      ctx.fillStyle = LBL.bg;
      ctx.fillRect(0, 0, w, h);
      drawCustomImage(ctx, l, w, h, img);
      if (opts.template === 'vilt') drawVilt(ctx, l, w, h, opts.weightByHand, logo);
      else drawEquip(ctx, l, w, h, opts.equipEmojis ?? {}, logo);
      ctx.restore();
    }
    out.push({ wMm: page.wMm, hMm: page.hMm, jpeg: await canvasJpeg(canvas), pxW: canvas.width, pxH: canvas.height });
  }
  // Släpp canvasminnet direkt (iOS har ett tak för canvas totalt)
  canvas.width = canvas.height = 0;
  return buildImagePdf(out);
}
